'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { InlineLoading } from '@carbon/react';
import CarbonPagination from '@/components/Pagination/Pagination';
import ListCardComponent from '@/components/Cards/ListCardComponent/listCardComponent';
import { getAllTaxonomies } from '@/apis/TaxonomyAPIs';
import { formatDateString, formatName } from '@/utils/formattingStrings';
import contentTypeDefaultImage from '@/../public/images/defaultImg';
import SortDropdown, { SortOrder } from '@/components/SortDropdown/SortDropdown';
import { PaginationSEOLinks } from '@/components/Pagination/PaginationSEOLinks';

import type { ContentItem, PaginatedResponse, Taxonomy } from '@/app/types';
import { appendHostAndBasePath } from '@/utils/urls';
import { fetchContentListItems } from './PaginationUtils';

interface ContentPaginationPageProps {
  slug: string;
  pageNumber?: number;
  currentPage?: number;
  tagType?: string;
  rootPath?: string;
  defaultImage?: string;
  emptyMessage?: string;
  enableSort?: boolean;
  showDate?: boolean;
  showType: boolean;
  serverPageData: PaginatedResponse<ContentItem> | null;
  /** Resolved query params from the server (e.g. { sort: 'newest' }) */
  seoSearchParams?: Record<string, string>;
}

const SortablePaginatedContentClient: React.FC<ContentPaginationPageProps> = ({
  slug,
  tagType = '',
  rootPath = '',
  emptyMessage = 'No content found for this selection.',
  enableSort = false,
  showDate = false,
  showType = false,
  serverPageData,
  currentPage,
  seoSearchParams,
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  
  const defaultSort: SortOrder = slug === 'openprojects' ? 'title_ascending' : 'newest';
  const urlSort = searchParams.get('sort') as SortOrder | null;
  const [sortOrder, setSortOrder] = useState<SortOrder>(urlSort || defaultSort);
  const [taxonomies, setTaxonomies] = useState<Record<string, Taxonomy> | null>(null);
  const [taxonomiesLoading, setTaxonomiesLoading] = useState(true);

  const bootstrapData = useRef(true);

  // ----------------------------- Fetch Taxonomies -----------------------------
  useEffect(() => {
    let mounted = true;

    const fetchTaxonomies = async () => {
      try {
        const taxonomyData = await getAllTaxonomies();
        if (!mounted) return;

        if (Array.isArray(taxonomyData)) {
          const mapped = taxonomyData.reduce((acc: Record<string, Taxonomy>, item) => {
            if (item.slug) acc[item.slug] = item;
            return acc;
          }, {});
          setTaxonomies(mapped);
        } else {
          setTaxonomies({});
        }
      } catch (error) {
        console.error('Failed to load taxonomies:', error);
        setTaxonomies({});
      } finally {
        if (mounted) setTaxonomiesLoading(false);
      }
    };

    fetchTaxonomies();
    return () => {
      mounted = false;
    };
  }, []);

  // ----------------------------- Fetch Paginated Content -----------------------------
  const fetchContentListForPagination = useCallback(
    async (page: number, pageSize: number) => {
      
      if (
        bootstrapData.current &&
        page === 1 &&
        pageSize === 12 // your defaultPageSize
      ) {
        bootstrapData.current = false;
        return (
          serverPageData || {
            data: [],
            totalItems: 0,
            totalPages: 0,
          }
        );
      }

      bootstrapData.current = false;

      return fetchContentListItems(
        slug,
        tagType,
        page,
        pageSize,
        true,
        sortOrder,
        'updated_date',
      );
    },
    [serverPageData, slug, tagType, sortOrder],
  );

  // ----------------------------- State for controlled pagination -----------------------------
  const [pageSize, setPageSize] = useState(12);
  const [currentPageState, setCurrentPageState] = useState(currentPage || 1);
  const [paginationKey, setPaginationKey] = useState(0);

  // ----------------------------- Helper: Get Image -----------------------------
  const getImageSrc = (imageUrl?: string, ImgSlug?: string): string =>
    imageUrl || contentTypeDefaultImage[ImgSlug as keyof typeof contentTypeDefaultImage] || '';

  // ----------------------------- 🚫 BLOCK RENDER UNTIL READY -----------------------------
  if (taxonomiesLoading || !taxonomies) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem', minHeight: '400px' }}>
        <InlineLoading description="Loading content..." />
      </div>
    );
  }


  const handleSortChange = (newSortOrder: SortOrder) => {
    
    // Reset bootstrap flag to force fresh data fetch
    bootstrapData.current = false;
    
    // Update sort order state
    setSortOrder(newSortOrder);
    
    // Reset to page 1 when sorting changes
    setCurrentPageState(1);
    
    // Force pagination component to re-render and refetch data
    setPaginationKey(prev => prev + 1);
    
    const params = new URLSearchParams(searchParams.toString());
    if (newSortOrder !== defaultSort) {
      params.set('sort', newSortOrder);
    } else {
      params.delete('sort');
    }
    params.delete('page');
    
    const queryString = params.toString();
    // Ensure trailing slash is present to match canonical URL format (/articles/)
    let pathname = window.location.pathname;
    if (!pathname.endsWith('/')) {
      pathname += '/';
    }
    const newUrl = queryString ? `${pathname}?${queryString}` : pathname.replace(/\/$/, '');
    
    // console.log('🔄 Sort Change Navigation:', {
    //   from: `${window.location.pathname}${window.location.search}`,
    //   to: newUrl,
    //   sortOrder: newSortOrder,
    //   hasTrailingSlash: pathname.endsWith('/'),
    //   timestamp: new Date().toISOString()
    // });
    
    router.push(newUrl, { scroll: false });
  };

  // ----------------------------- RENDER -----------------------------
  // Calculate totalPages if not provided by server
  // const pageSize = serverPageData?.data?.length || 12;
  const totalItems = serverPageData?.totalItems || 0;
const totalPages = serverPageData?.totalPages || 0;
  // When slug is 'all', don't include it in the baseUrl
  const includeSlug = slug !== 'all';
  
  const baseUrl = rootPath && tagType
    ? includeSlug
      ? `/${rootPath}/${tagType}/${slug}`
      : `/${rootPath}/${tagType}`
    : tagType
    ? includeSlug
      ? `/${tagType}/${slug}`
      : `/${tagType}`
    : includeSlug
    ? `/${slug}`
    : '/';

  return (
    <div className="content-pagination-page">
      {enableSort && (
        <SortDropdown
          sortOrder={sortOrder}
          onChange={handleSortChange}
          labelText=""
          className="sorting-box"
        />
      )}

      <CarbonPagination<ContentItem>
        key={paginationKey}
        totalItems={totalItems}
        fetchData={fetchContentListForPagination}
        currentPage={currentPage}
        defaultPageSize={12}
        pageSizeOptions={[12, 24, 36]}
        emptyMessage={emptyMessage}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => {
          // Reset bootstrap flag when page size changes
          bootstrapData.current = false;
          setPageSize(newPageSize);
          setCurrentPageState(1);
          // Force re-render of pagination component to trigger data fetch
          setPaginationKey(prev => prev + 1);
        }}
        onPageChange={(newPage) => {
          setCurrentPageState(newPage);
        }}
        renderItem={(item: ContentItem | ContentItem[], idx?: number) => {
          const contentItem = Array.isArray(item) ? item[0] : item;
          return (
            <ListCardComponent
              key={contentItem.id ?? idx}
              date={
                showDate && (contentItem?.updated_date || contentItem?.publish_date)
                  ? formatDateString((contentItem.updated_date || contentItem.publish_date)!, true)
                  : ''
              }
              contentType={showType ? formatName(contentItem.content_type) : ''}
              title={contentItem.title ?? contentItem.name ?? 'Untitled'}
              excerpt={contentItem.excerpt ?? contentItem.summary ?? ''}
              subtitle={contentItem.subtitle ?? ''}
              link={appendHostAndBasePath(`/${contentItem.content_type}/${contentItem?.slug}`)}
              externalLink={contentItem.code_links_to || ''}
              imageSrc={getImageSrc(contentItem.images?.card_image, contentItem.content_type)}
              primaryCategory={contentItem.primary_category || ''}
              taxonomies={taxonomies}
              subtype={contentItem.subtype}
              course_portal={contentItem.course_portal}
              content_type={contentItem.content_type}
            />
          );
        }}
      />
      <PaginationSEOLinks
        baseUrl={baseUrl}
        totalPages={totalPages}
        searchParams={seoSearchParams}
      />
    </div>
  );
};

export default SortablePaginatedContentClient;
